import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuthStore } from "../../Store/auth";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiRefreshCw,
  FiDownload,
  FiPhone,
  FiMail,
  FiMessageCircle,
  FiClock,
  FiCalendar,
  FiEye,
} from "react-icons/fi";
import { API_ORIGIN } from "../../config/api";

const API = API_ORIGIN;

const ymd = (d) => new Date(d).toISOString().slice(0, 10);
const parseSlotMinutes = (slot) => {
  const m = /^(\d{2}):(\d{2})\s*-\s*(\d{2}):(\d{2})$/.exec(String(slot).trim());
  if (!m) return Number.MAX_SAFE_INTEGER;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};
const statusMeta = {
  0: { label: "Pending", cls: "bg-yellow-100 text-yellow-800" },
  1: { label: "Accepted", cls: "bg-green-100 text-green-800" },
  2: { label: "Cancelled", cls: "bg-red-100 text-red-800" },
  3: { label: "Deleted", cls: "bg-gray-200 text-gray-700" },
};
const exportCSV = (rows) => {
  if (!rows?.length) return toast.info("Nothing to export.");
  const headers = ["Student Name","Email","Phone","Date","Time Slot","Status","Created At"];
  const data = rows.map((b) => [
    b.studentName || "",
    b.studentEmail || "",
    b.studentPhone || "",
    new Date(b.bookingDate).toLocaleDateString(),
    b.timeSlot || "",
    statusMeta[b.status]?.label || "",
    new Date(b.createdAt || b.bookingDate).toLocaleString(),
  ]);
  const csv = [headers, ...data].map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `bookings_${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
};

function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const { token, coachId, logout } = useAuthStore();
  const navigate = useNavigate();

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/Booking/coach/${coachId}`, authHeaders);
      const list = Array.isArray(res.data) ? res.data : [];
      list.sort((a, b) => {
        const da = new Date(a.bookingDate).getTime();
        const db = new Date(b.bookingDate).getTime();
        if (db !== da) return db - da;
        return parseSlotMinutes(a.timeSlot) - parseSlotMinutes(b.timeSlot);
      });
      setBookings(list);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      console.error(err);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [coachId, authHeaders, logout]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const filtered = useMemo(() => {
    let arr = bookings;
    if (filterDate) arr = arr.filter((b) => ymd(b.bookingDate) === filterDate);
    if (filterStatus !== "all") arr = arr.filter((b) => String(b.status) === filterStatus);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      arr = arr.filter((b) =>
        (b.studentName || "").toLowerCase().includes(q) ||
        (b.studentEmail || "").toLowerCase().includes(q) ||
        (b.studentPhone || "").toLowerCase().includes(q)
      );
    }
    const map = new Map();
    for (const b of arr) {
      const key = ymd(b.bookingDate);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    }
    return [...map.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([date, items]) => ({
        date,
        items: items.sort((x, y) => parseSlotMinutes(x.timeSlot) - parseSlotMinutes(y.timeSlot)),
      }));
  }, [bookings, filterDate, filterStatus, query]);

  const StatusBadge = ({ status }) => {
    const meta = statusMeta[status] || statusMeta[0];
    return <span className={`px-3 py-1 rounded-full text-sm font-semibold ${meta.cls}`}>{meta.label}</span>;
  };

  const counts = useMemo(() => {
    const c = { total: bookings.length, p: 0, a: 0, c: 0 };
    bookings.forEach((b) => { if (b.status === 0) c.p++; if (b.status === 1) c.a++; if (b.status === 2) c.c++; });
    return c;
  }, [bookings]);

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-3xl font-extrabold">📅 My Bookings</h2>
          <p className="text-sm text-gray-500">
            Total {counts.total} • Pending {counts.p} • Accepted {counts.a} • Cancelled {counts.c}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search name, email, phone"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg w-full sm:w-72"
            />
          </div>

          <div className="flex gap-2">
            <div className="flex items-center gap-2">
              <label htmlFor="dateFilter" className="text-sm text-gray-600 whitespace-nowrap">
                <FiCalendar className="inline mr-1" />
                Date
              </label>
              <input
                type="date"
                id="dateFilter"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="p-2 border rounded-lg"
              />
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="statusFilter" className="text-sm text-gray-600">Status</label>
              <select
                id="statusFilter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="p-2 border rounded-lg"
              >
                <option value="all">All</option>
                <option value="0">Pending</option>
                <option value="1">Accepted</option>
                <option value="2">Cancelled</option>
                <option value="3">Deleted</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={fetchBookings} className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2">
              <FiRefreshCw /> Refresh
            </button>
            <button
              onClick={() => exportCSV(filtered.flatMap((g) => g.items))}
              className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
            >
              <FiDownload /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse h-20 bg-gray-200 rounded-lg" />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center text-gray-600 border rounded-xl p-8 bg-white">
          No bookings found.
          <div className="text-sm text-gray-500 mt-1">Try changing filters or refresh.</div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-8">
          {filtered.map(({ date, items }) => (
            <section key={date} className="space-y-3">
              <div className="sticky top-0 z-10 bg-gray-50/70 backdrop-blur px-2 py-1 rounded">
                <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FiCalendar /> {new Date(date).toLocaleDateString()}
                  <span className="text-gray-400">•</span>
                  <FiClock /> {items.length} booking{items.length > 1 ? "s" : ""}
                </span>
              </div>

              <div className="grid gap-4">
                {items.map((b) => (
                  <div key={b.id} className="border p-5 rounded-xl shadow-sm bg-white text-gray-800 relative">
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <h3 className="text-lg font-bold">{b.studentName || "—"}</h3>
                        <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                          <p><strong>📧 Email:</strong> {b.studentEmail || "—"}</p>
                          <p><strong>📞 Phone:</strong> {b.studentPhone || "—"}</p>
                          <p><strong>📅 Date:</strong> {new Date(b.bookingDate).toLocaleDateString()}</p>
                          <p><strong>⏰ Time:</strong> {b.timeSlot || "—"}</p>
                        </div>
                      </div>
                      <StatusBadge status={b.status} />
                    </div>

                    {b.locked ? <p className="mt-3 text-sm text-blue-700">Subscribe to Lead Access to view client details and manage this request.</p> : null}
                    {/* Quick contact */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {b.studentEmail && (
                        <a href={`mailto:${b.studentEmail}`} className="px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50 flex items-center gap-2">
                          <FiMail /> Email
                        </a>
                      )}
                      {b.studentPhone && (
                        <>
                          <a href={`tel:${b.studentPhone}`} className="px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50 flex items-center gap-2">
                            <FiPhone /> Call
                          </a>
                          <a
                            href={`https://wa.me/${String(b.studentPhone).replace(/\D/g, "")}`}
                            target="_blank" rel="noreferrer"
                            className="px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <FiMessageCircle /> WhatsApp
                          </a>
                        </>
                      )}
                    </div>

                    {/* Single CTA: View Booking */}
                    <div className="mt-4 flex">
                      <button
                        onClick={() => navigate(b.locked ? "/dashboard/subscription" : `/dashboard/bookings/${b.id}`)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 w-full sm:w-auto"
                      >
                        <FiEye /> {b.locked ? "Unlock lead" : "View booking"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export default Bookings;
